'use client'

import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CloseIcon } from '@repo/ui/CloseIcon'
import {
  DataGridPro,
  gridClasses,
  GridColDef,
  GridPaginationModel,
  GridRenderHeaderFilterProps,
  GridSortModel
} from '@mui/x-data-grid-pro'
import { useFormsStore } from '@repo/store/useFormsStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { useNavigate } from 'react-router-dom'
import { useThirdPartyFormsStore } from '@repo/store/useThirdPartyFormsStore'
import { useWindowSize } from '@uidotdev/usehooks'
import MUIBox from '@mui/material/Box'
import MUIButton from '@mui/material/Button'
import MUIIconButton from '@mui/material/IconButton'
import MUIMenuItem from '@mui/material/MenuItem'
import MUIPaper from '@mui/material/Paper'
import MUISelect from '@mui/material/Select'
import MUIStack from '@mui/material/Stack'
import MUITextField from '@mui/material/TextField'
import MUITypography from '@mui/material/Typography'

type WorkflowType = { workflowTypeId: string; description: string }

type FormRow = {
  formId: string
  name: string
  description: string
  workTypeId?: string | null
}

const GLOBAL_FILTER_VALUE = '__global__'
const FILTER_DEBOUNCE_MS = 300

const filterFieldSx = {
  width: '100%',
  backgroundColor: '#fff',
  '& .MuiOutlinedInput-root': {
    height: 32,
    fontSize: '0.875rem',
    backgroundColor: '#fff',
    pr: '28px',
    mb: '0 !important'
  },
  '& .MuiSelect-select': { py: '6px', pl: '8px', backgroundColor: '#fff', mb: 0 }
}

const clearIconButtonSx = {
  position: 'absolute',
  right: 6,
  top: '50%',
  transform: 'translateY(-50%)',
  padding: 0,
  height: 16,
  width: 16,
  minWidth: 0,
  zIndex: 1
}

const SORT_FIELD_TO_SERVER_COLUMN: Record<string, string> = {
  name: 'name',
  description: 'description',
  type: 'workTypeId'
}

type ContainsFilterProps = {
  onValueChange: (value: string) => void
  testId: string
}

const ContainsFilter = ({ onValueChange, testId }: ContainsFilterProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [hasValue, setHasValue] = useState(false)

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value
    setHasValue(v.length > 0)
    onValueChange(v)
  }

  const handleClear = () => {
    if (inputRef.current) inputRef.current.value = ''
    setHasValue(false)
    onValueChange('')
    inputRef.current?.focus()
  }

  return (
    <MUIBox sx={{ position: 'relative', width: '90%', display: 'flex', alignItems: 'center' }}>
      <MUITextField
        data-testid={testId}
        placeholder="Contains"
        size="small"
        defaultValue=""
        inputRef={inputRef}
        onChange={handleChange}
        sx={filterFieldSx}
      />
      {hasValue && (
        <MUIIconButton aria-label="clear filter" onClick={handleClear} sx={clearIconButtonSx}>
          <CloseIcon sx={{ fill: 'black', height: '0.65rem', width: '0.65rem' }} />
        </MUIIconButton>
      )}
    </MUIBox>
  )
}

type TypeFilterProps = {
  workflowTypes: WorkflowType[]
  workflowTypeMap: Map<string, string>
  onValueChange: (values: string[]) => void
}

const TypeFilter = ({ workflowTypes, workflowTypeMap, onValueChange }: TypeFilterProps) => {
  const [selected, setSelected] = useState<string[]>([])

  const handleChange = (e: any) => {
    const v = e.target.value
    const next = typeof v === 'string' ? v.split(',') : (v as string[])
    setSelected(next)
    onValueChange(next)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    setSelected([])
    onValueChange([])
  }

  const labelFor = (v: string) => (v === GLOBAL_FILTER_VALUE ? 'Global' : (workflowTypeMap.get(v) ?? '—'))

  return (
    <MUIBox sx={{ position: 'relative', width: '90%', display: 'flex', alignItems: 'center' }}>
      <MUISelect
        data-testid="FormEvoFieldMapping-type-filter"
        multiple
        displayEmpty
        value={selected}
        onChange={handleChange}
        renderValue={(s) => {
          const arr = s as string[]
          if (!arr || arr.length === 0) {
            return (
              <MUITypography component="span" sx={{ color: 'text.disabled', fontSize: '0.875rem' }}>
                Select
              </MUITypography>
            )
          }
          if (arr.length === 1) return labelFor(arr[0])
          return `${labelFor(arr[0])} + ${arr.length - 1}`
        }}
        sx={{
          ...filterFieldSx,
          height: 32,
          mb: 0,
          '& .MuiSelect-select': { py: '6px', pl: '8px', fontSize: '0.875rem', backgroundColor: '#fff' }
        }}
      >
        <MUIMenuItem value={GLOBAL_FILTER_VALUE}>Global</MUIMenuItem>
        {(workflowTypes ?? []).map((w) => (
          <MUIMenuItem key={w.workflowTypeId} value={w.workflowTypeId}>
            {`Worktype: ${w.description}`}
          </MUIMenuItem>
        ))}
      </MUISelect>
      {selected.length > 0 && (
        <MUIIconButton aria-label="clear filter" onClick={handleClear} sx={{ ...clearIconButtonSx, right: 28 }}>
          <CloseIcon sx={{ fill: 'black', height: '0.65rem', width: '0.65rem' }} />
        </MUIIconButton>
      )}
    </MUIBox>
  )
}

export const FormEvoFieldMappingModule = () => {
  const { getAllFormsBySearchCriteria } = useFormsStore()
  const { workflowTypes, fetchWorkflowTypes } = useLookupsStore() as {
    workflowTypes: WorkflowType[]
    fetchWorkflowTypes: () => Promise<unknown>
  }
  const { resetMappedFormData } = useThirdPartyFormsStore() as {
    resetMappedFormData: () => void
  }
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()
  const { height: windowHeight } = useWindowSize()
  const navigate = useNavigate()

  const handleNewClick = () => {
    resetMappedFormData()
    navigate('?wizard=FormEvo-FormMapping&stage=formevo-form-mapping&step=select-worktype')
  }

  const [rows, setRows] = useState<FormRow[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [tableHeight, setTableHeight] = useState(windowHeight ? windowHeight - 216 : 0)

  const [pageSize, setPageSize] = useState(25)
  const [startAtIndex, setStartAtIndex] = useState(0)

  const [nameFilter, setNameFilter] = useState('')
  const [descriptionFilter, setDescriptionFilter] = useState('')
  const [selectedTypeValues, setSelectedTypeValues] = useState<string[]>([])
  const [sortModel, setSortModel] = useState<GridSortModel>([])

  const nameDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const descriptionDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const offset = pageTitleBlockIsVisible ? 360 : 160
    if (windowHeight) setTableHeight(windowHeight - offset)
  }, [windowHeight, pageTitleBlockIsVisible])

  useEffect(() => {
    fetchWorkflowTypes()
  }, [fetchWorkflowTypes])

  useEffect(
    () => () => {
      if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current)
      if (descriptionDebounceRef.current) clearTimeout(descriptionDebounceRef.current)
    },
    []
  )

  const handleNameInputChange = useCallback((value: string) => {
    if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current)
    nameDebounceRef.current = setTimeout(() => {
      setNameFilter(value.trim())
      setStartAtIndex(0)
    }, FILTER_DEBOUNCE_MS)
  }, [])

  const handleDescriptionInputChange = useCallback((value: string) => {
    if (descriptionDebounceRef.current) clearTimeout(descriptionDebounceRef.current)
    descriptionDebounceRef.current = setTimeout(() => {
      setDescriptionFilter(value.trim())
      setStartAtIndex(0)
    }, FILTER_DEBOUNCE_MS)
  }, [])

  const handleTypeValuesChange = useCallback((values: string[]) => {
    setSelectedTypeValues(values)
    setStartAtIndex(0)
  }, [])

  const workflowTypeMap = useMemo(
    () => new Map((workflowTypes ?? []).map((w) => [w.workflowTypeId, w.description])),
    [workflowTypes]
  )

  const typeLabel = useCallback(
    (row: FormRow): string => {
      if (!row.workTypeId) return 'Global'
      return `Worktype: ${workflowTypeMap.get(row.workTypeId) ?? '—'}`
    },
    [workflowTypeMap]
  )

  // Server endpoint accepts a single workTypeId. For multi-select, fetch unfiltered
  // and narrow client-side on the returned page.
  const onlyServerWorkTypeId =
    selectedTypeValues.length === 1 && selectedTypeValues[0] !== GLOBAL_FILTER_VALUE ? selectedTypeValues[0] : null

  const orderByColumns = useMemo(() => {
    const mapped = sortModel
      .map((s) => {
        const columnName = SORT_FIELD_TO_SERVER_COLUMN[s.field]
        if (!columnName) return null
        return { columnName, direction: s.sort === 'asc' ? 0 : 1 }
      })
      .filter(Boolean) as { columnName: string; direction: number }[]
    return mapped.length > 0 ? mapped : null
  }, [sortModel])

  useEffect(() => {
    let cancelled = false
    const fetchData = async () => {
      setLoading(true)
      try {
        const response: any = await getAllFormsBySearchCriteria({
          startAtIndex,
          pageSize,
          orderByColumns,
          formCategoryId: null,
          name: nameFilter || null,
          description: descriptionFilter || null,
          workTypeId: onlyServerWorkTypeId
        })

        if (cancelled) return

        const items: FormRow[] = Array.isArray(response) ? response : (response?.rows ?? response?.items ?? [])
        const total: number =
          response?.totalCount ?? response?.total ?? (Array.isArray(response) ? response.length : items.length)

        let displayed = items
        if (selectedTypeValues.length > 1) {
          const wantsGlobal = selectedTypeValues.includes(GLOBAL_FILTER_VALUE)
          const wantedWorkTypeIds = new Set(selectedTypeValues.filter((v) => v !== GLOBAL_FILTER_VALUE))
          displayed = items.filter((row) => (row.workTypeId ? wantedWorkTypeIds.has(row.workTypeId) : wantsGlobal))
        }

        setRows(displayed)
        setTotalCount(total)
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching FormEvo field mapping forms:', error)
          setRows([])
          setTotalCount(0)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchData()
    return () => {
      cancelled = true
    }
  }, [
    getAllFormsBySearchCriteria,
    startAtIndex,
    pageSize,
    nameFilter,
    descriptionFilter,
    onlyServerWorkTypeId,
    selectedTypeValues,
    orderByColumns
  ])

  const columns = useMemo<GridColDef<FormRow>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Form name',
        flex: 1,
        minWidth: 160,
        sortable: true,
        filterable: false,
        disableColumnMenu: true,
        hideable: false,
        renderCell: (params) => (
          <MUITypography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
            {params.row.name}
          </MUITypography>
        ),
        renderHeaderFilter: (_params: GridRenderHeaderFilterProps) => (
          <ContainsFilter onValueChange={handleNameInputChange} testId="FormEvoFieldMapping-name-filter" />
        )
      },
      {
        field: 'description',
        headerName: 'Form description',
        flex: 1,
        minWidth: 200,
        sortable: true,
        filterable: false,
        disableColumnMenu: true,
        hideable: false,
        renderCell: (params) => (
          <MUITypography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {params.row.description}
          </MUITypography>
        ),
        renderHeaderFilter: (_params: GridRenderHeaderFilterProps) => (
          <ContainsFilter
            onValueChange={handleDescriptionInputChange}
            testId="FormEvoFieldMapping-description-filter"
          />
        )
      },
      {
        field: 'type',
        headerName: 'Type',
        flex: 2.5,
        minWidth: 280,
        sortable: true,
        filterable: false,
        disableColumnMenu: true,
        hideable: false,
        valueGetter: (_value, row) => typeLabel(row),
        renderCell: (params) => (
          <MUITypography variant="body2" sx={{ fontSize: '0.875rem' }}>
            {params.value}
          </MUITypography>
        ),
        renderHeaderFilter: (_params: GridRenderHeaderFilterProps) => (
          <TypeFilter
            workflowTypes={workflowTypes ?? []}
            workflowTypeMap={workflowTypeMap}
            onValueChange={handleTypeValuesChange}
          />
        )
      }
    ],
    [
      handleNameInputChange,
      handleDescriptionInputChange,
      handleTypeValuesChange,
      workflowTypes,
      workflowTypeMap,
      typeLabel
    ]
  )

  const paginationModel: GridPaginationModel = {
    page: pageSize > 0 ? Math.floor(startAtIndex / pageSize) : 0,
    pageSize
  }

  return (
    <MUIStack data-testid="FormEvoFieldMappingModule" gap={2} sx={{ px: '2rem' }}>
      <MUIStack direction="row" justifyContent="flex-end">
        <MUIButton
          data-testid="FormEvoFieldMapping-new-button"
          variant="outlined"
          onClick={handleNewClick}
          sx={{
            backgroundColor: 'background.main',
            color: '#051C33',
            borderColor: 'rgba(5, 28, 51, 0.23)',
            fontSize: '0.75rem',
            minWidth: 100,
            '&:hover': {
              backgroundColor: 'secondary.light',
              borderColor: 'rgba(5, 28, 51, 0.23)',
              color: 'primary.contrastText'
            }
          }}
        >
          NEW
        </MUIButton>
      </MUIStack>

      <MUIPaper
        sx={{
          position: 'relative',
          fontSize: '0.875rem',
          width: '100%',
          height: `calc(${tableHeight}px - 20px)` || 'auto',
          boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0)'
        }}
      >
        <DataGridPro
          columns={columns}
          rows={rows}
          getRowId={(row) => row.formId}
          headerFilters
          loading={loading}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={(newModel) => {
            setPageSize(newModel.pageSize)
            setStartAtIndex(newModel.page * newModel.pageSize)
          }}
          pageSizeOptions={[5, 10, 25, 50]}
          rowCount={totalCount}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model)
            setStartAtIndex(0)
          }}
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableRowSelectionOnClick
          slotProps={{
            loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' }
          }}
          sx={{
            border: 'none',
            [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
            [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
              outline: 'none'
            },
            [`& .${gridClasses.columnHeader}`]: { fontSize: '0.875rem' },
            [`& .${gridClasses.columnHeaderTitle}`]: { fontWeight: 600 },
            [`& .MuiDataGrid-cell`]: { alignContent: 'center' },
            [`& .MuiTablePagination-selectLabel`]: { display: 'block !important' },
            [`& .MuiTablePagination-input`]: {
              display: 'inline-flex !important',
              mb: 0,
              ml: 0,
              mt: '8px',
              fontSize: '0.875rem',
              [`& .MuiTablePagination-select`]: {
                padding: '0 !important',
                ml: '4px',
                mr: '20px',
                fontSize: '0.75rem',
                [`&:focus`]: { backgroundColor: 'transparent' }
              },
              [`& .MuiSvgIcon-root`]: { width: '1.5rem', height: '1.5rem', top: 'calc(50% - 12px)' }
            },
            [`& .MuiTablePagination-actions`]: { [`& .MuiButtonBase-root`]: { padding: 0, top: '4px' } },
            [`& .MuiDataGrid-filler`]: { display: 'none' }
          }}
        />
      </MUIPaper>
    </MUIStack>
  )
}
